"use client"
import { Brand } from "@/api/generated/BMWInterfaces"
import { resetFilterClick } from "@/lib/gtm"
import colors from "@/lib/theme/colors"
import styled from "@emotion/styled"
import { Button, Checkbox, FormControlLabel, Typography } from "@mui/material"
import Image from "next/image"
import SliderFilter from "../Homepage/Slider"
import CheckBoxIcon from "../shared/icons/CheckBoxIcon"
import CheckboxCheckedIcon from "../shared/icons/CheckBoxIcon copy"
import { VehicleType } from "./Search"

const FilterBox = styled.div`
  display: flex;
  width: 300px;
  flex-direction: column;
  gap: 32px;
  border-left: 1px solid #f2f3ed;
  padding-left: 5px;
`

const SyledFilterTop = styled.div`
  padding: 8px 0%;
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid #f2f3ed;
`

export const StyledFilterButton = styled(Button)`
  padding: 8px 16px;
  font-size: 16px;
  color: ${colors.directBlack};
  text-decoration-line: underline;
  :hover {
    background-color: transparent;
  }
`

const FilterIconFlex = styled.div`
  display: flex;
  gap: 2px;
  align-items: center;
`

const SelectionBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  padding-bottom: 16px;
  border-bottom: 1px solid #efefef;
`

const FilterArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const CheckboxesContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px 8px;
`

const StyledFormControlLabel = styled(FormControlLabel)<{ checked?: boolean }>`
  margin: 0;
  display: flex;
  gap: 4px;
  align-items: center;
  padding: 6px 8px;
  border: 1px solid ${(props) => (props.checked ? "#231F20" : colors.directVeryLightGray)};
  border-radius: 6px;
  & .MuiCheckbox-root {
    padding: 0;
    img {
      object-fit: contain;
    }
    svg {
      display: none;
    }
  }
  & .MuiTypography-root {
    font-size: 18px;
    font-weight: 400;
    font-size: 18px;
    line-height: 100%;
    letter-spacing: 0em;
  }
`

const PriceArea = styled.div`
  padding-top: 24px;
  padding-bottom: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-left: 16px;
`

const SliderContainer = styled.div`
  padding-top: 40px;
  padding-left: 40px;
  padding-right: 10px;
`

type Props = {
  brands: Brand[]
  types: VehicleType[]
  engineTypes: { id: number; name: string }[]
  clearAllFilters: () => Promise<void>
  chosenBrand: string[]
  chosenType: string[]
  chosenEngineType: string[]
  handleChangeChosenBrand: (brand: Brand, autoUpdate: boolean) => () => void
  handleChangeChosenType: (type: VehicleType, autoUpdate: boolean) => () => void
  handleChangeChosenEngineType: (
    engineType: { id: number; name: string },
    autoUpdate: boolean,
  ) => () => void
  minMaxPrice: number[]
  handleChange: (event: Event, newValue: number | number[], autoUpdate?: boolean) => void
  brandMax: number
  brandMin: number
  getNewValuesFilter: () => void
}

export const FilterDesktop = ({
  brands,
  types,
  engineTypes,
  chosenBrand,
  chosenType,
  chosenEngineType,
  brandMax,
  brandMin,
  minMaxPrice,
  clearAllFilters,
  handleChangeChosenBrand,
  handleChangeChosenType,
  handleChangeChosenEngineType,
  handleChange,
}: Props): React.ReactElement => {
  const hasActiveFilters =
    chosenBrand.length > 0 ||
    chosenType.length > 0 ||
    chosenEngineType.length > 0 ||
    minMaxPrice[0] !== brandMin ||
    minMaxPrice[1] !== brandMax

  return (
    <FilterBox>
      <FilterArea>
        <SyledFilterTop>
          <FilterIconFlex>
            <Image src="/assets/filter.svg" alt="filter" width={20} height={20} />
            <Typography variant="body2" color={"black"}>
              סינון
            </Typography>
          </FilterIconFlex>
          {hasActiveFilters && (
            <StyledFilterButton
              onClick={() => {
                clearAllFilters()
                resetFilterClick()
              }}
              variant="text"
            >
              איפוס
            </StyledFilterButton>
          )}
        </SyledFilterTop>
        <SelectionBox>
          <Typography variant="subtitle1">סוג רכב</Typography>
          <CheckboxesContainer>
            {types.map((type, index) => {
              const isChecked = chosenType.some((val) => val === type.value?.toString())
              return (
                <StyledFormControlLabel
                  key={index}
                  checked={isChecked}
                  control={
                    <Checkbox
                      checked={isChecked}
                      onChange={handleChangeChosenType(type, true)}
                      icon={<CheckBoxIcon />}
                      checkedIcon={<CheckboxCheckedIcon />}
                    />
                  }
                  label={type.label}
                />
              )
            })}
          </CheckboxesContainer>
          <Typography variant="subtitle1">מותג רכב</Typography>
          <CheckboxesContainer>
            {brands.map((brand, index) => {
              const isChecked =
                chosenBrand.indexOf(brand.id.toString()) > -1 ||
                (brand.id.toString() === "3" && chosenBrand.indexOf("2") > -1)
              return (
                <StyledFormControlLabel
                  key={index}
                  checked={isChecked}
                  control={
                    <Checkbox
                      checked={isChecked}
                      onChange={handleChangeChosenBrand(brand, true)}
                      icon={
                        brand.logo?.imageUrl && (
                          <Image
                            src={brand.logo?.imageUrl || ""}
                            alt={brand.name || "brand logo"}
                            width={20}
                            height={20}
                          />
                        )
                      }
                      checkedIcon={
                        brand.logo?.imageUrl && (
                          <Image
                            src={brand.logo?.imageUrl || ""}
                            alt={brand.name || "brand logo"}
                            width={20}
                            height={20}
                          />
                        )
                      }
                    />
                  }
                  label={brand.name}
                />
              )
            })}
          </CheckboxesContainer>
          <Typography variant="subtitle1">סוג מנוע</Typography>
          <CheckboxesContainer>
            {engineTypes.map((engineType, index) => {
              const isChecked = chosenEngineType.indexOf(engineType.id.toString()) > -1
              return (
                <StyledFormControlLabel
                  key={index}
                  checked={isChecked}
                  control={
                    <Checkbox
                      checked={isChecked}
                      onChange={handleChangeChosenEngineType(engineType, true)}
                      icon={<CheckBoxIcon />}
                      checkedIcon={<CheckboxCheckedIcon />}
                    />
                  }
                  label={engineType.name}
                />
              )
            })}
          </CheckboxesContainer>
        </SelectionBox>
        <PriceArea>
          <Typography variant="subtitle1">חיפוש לפי תקציב</Typography>
          <SliderContainer>
            <SliderFilter
              minMaxPrice={minMaxPrice}
              handleChange={(event: Event, newValue: number | number[]) =>
                handleChange(event, newValue, true)
              }
              brandMax={brandMax}
              brandMin={brandMin}
            />
          </SliderContainer>
        </PriceArea>
      </FilterArea>
    </FilterBox>
  )
}
